import { useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

const navItems = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

export default function Header({ pathname: shownPath }: { pathname?: string }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // Theme follows the page actually on screen (lags the URL during the exit fade)
  const pathname = shownPath ?? location.pathname;
  // Initial value matches the first frame (scroll is reset to top, hero sits under
  // the header on Home) so the header never paints white and then fades to orange.
  const [onHero, setOnHero] = useState(() => pathname === '/');

  // Header takes the page colour while the hero (all three stages) is under it
  // Leaving the hero switches to white immediately. Going back to orange must be
  // confirmed for a short while: hero stage snap-backs, momentum holds and
  // Back/Forward scroll restores can put the hero under the header for a frame
  // or two, which used to flash the orange theme.
  const onHeroRef = useRef(onHero);
  useLayoutEffect(() => {
    let confirmTimer = 0;
    const apply = (v: boolean) => { onHeroRef.current = v; setOnHero(v); };
    const measure = () => {
      const hero = document.querySelector('[data-header-orange]');
      return !!hero && hero.getBoundingClientRect().bottom > 70;
    };
    const update = (immediate = false) => {
      const want = measure();
      if (!want) {
        window.clearTimeout(confirmTimer); confirmTimer = 0;
        if (onHeroRef.current) apply(false);
        return;
      }
      if (onHeroRef.current) return;
      // At the very top the hero is truly under the header — no need to confirm
      if (immediate || window.scrollY <= 1) { apply(true); return; }
      if (confirmTimer) return;
      confirmTimer = window.setTimeout(() => {
        confirmTimer = 0;
        if (measure()) apply(true);
      }, 220);
    };
    // Genuinely at the top of a page: no reason to wait
    update(window.scrollY <= 1);
    // At most one measurement per frame
    let raf = 0;
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; update(); }); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(confirmTimer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [pathname]);

  return (
    <header className={`sticky top-0 z-50 bg-background-50 transition-colors duration-500 ${onHero ? 'theme-orange' : ''}`}>
      <div className="wrap flex items-center justify-between h-[64px] md:h-[68px]">
        <Link to="/" aria-label="Morrow People home" className="flex items-baseline gap-2.5 md:gap-3 whitespace-nowrap cursor-pointer text-foreground-950 focus-ring">
          <span className="text-accent-500 font-display font-semibold text-[26px] md:text-[30px] leading-none tracking-[-0.01em]">MORROW</span>
          <span className="font-label text-[10px] md:text-[11px] font-medium tracking-[0.32em] text-foreground-600 translate-y-[-2px]">PEOPLE</span>
        </Link>

        <div className="hidden md:flex items-center gap-10 lg:gap-16">
          <nav aria-label="Main navigation" className="flex items-center gap-8 lg:gap-14">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `group relative font-label text-[15px] lg:text-[16px] whitespace-nowrap cursor-pointer transition-colors duration-200 ease-ui focus-ring py-1.5 ${
                    isActive ? 'text-foreground-950' : 'text-foreground-700 hover:text-foreground-950'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span className={`absolute left-0 bottom-0 h-px w-full bg-foreground-950 origin-left transition-transform duration-200 ease-ui ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          className="md:hidden w-10 h-10 flex items-center justify-center text-2xl text-foreground-950 cursor-pointer whitespace-nowrap"
        >
          <i className={open ? 'ri-close-line' : 'ri-menu-line'}></i>
        </button>
      </div>

      {open && (
        <div id="mobile-menu" className="md:hidden absolute top-full left-0 right-0 bg-background-50 border-b border-background-200">
          <nav aria-label="Mobile navigation" className="wrap flex flex-col py-4">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-display font-medium text-[32px] py-2.5 border-b border-background-200 cursor-pointer ${isActive ? 'text-accent-600' : 'text-foreground-950'}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}